// Висячие фигуры и жизнеспособность взятия — машинерия для «Блестящего» хода.
// Порт из app.js v3 (~2239–2331).
import { Chess, type Square } from 'chess.js';

export const PIECE_VALUES: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };

export interface HangingPiece {
  square: Square;
  type: string;
  value: number;
  attackers: Square[];
  defenders: Square[];
}

function squareCoords(square: string): { f: number; r: number } | null {
  const f = 'abcdefgh'.indexOf(square?.[0] ?? '');
  const r = Number(square?.[1]) - 1;
  return f >= 0 && r >= 0 && r < 8 ? { f, r } : null;
}

function coordsSquare(f: number, r: number): Square | null {
  return f >= 0 && f < 8 && r >= 0 && r < 8 ? (('abcdefgh'[f]! + String(r + 1)) as Square) : null;
}

function pieceAttacksSquare(chess: Chess, from: Square, to: Square): boolean {
  if (from === to) return false;
  const piece = chess.get(from);
  if (!piece || piece.type === 'k') return false;
  const a = squareCoords(from);
  const b = squareCoords(to);
  if (!a || !b) return false;
  const df = b.f - a.f;
  const dr = b.r - a.r;
  if (piece.type === 'p') return Math.abs(df) === 1 && dr === (piece.color === 'w' ? 1 : -1);
  if (piece.type === 'n') return (Math.abs(df) === 1 && Math.abs(dr) === 2) || (Math.abs(df) === 2 && Math.abs(dr) === 1);
  const stepF = Math.sign(df);
  const stepR = Math.sign(dr);
  if (piece.type === 'b' && Math.abs(df) !== Math.abs(dr)) return false;
  if (piece.type === 'r' && df !== 0 && dr !== 0) return false;
  if (piece.type === 'q' && !(Math.abs(df) === Math.abs(dr) || df === 0 || dr === 0)) return false;
  let f = a.f + stepF;
  let r = a.r + stepR;
  while (f !== b.f || r !== b.r) {
    const sq = coordsSquare(f, r);
    if (!sq || chess.get(sq)) return false;
    f += stepF;
    r += stepR;
  }
  return true;
}

function attackersOf(chess: Chess, square: Square, color: 'w' | 'b'): Square[] {
  const out: Square[] = [];
  for (const row of chess.board()) {
    for (const piece of row) {
      if (!piece || piece.color !== color) continue;
      if (piece.type === 'k') continue;
      if (pieceAttacksSquare(chess, piece.square, square)) out.push(piece.square);
    }
  }
  return out;
}

function lowestAttackerValue(chess: Chess, square: Square, color: 'w' | 'b'): number | null {
  const vals = attackersOf(chess, square, color).map((sq) => PIECE_VALUES[chess.get(sq)?.type ?? ''] ?? 99);
  return vals.length ? Math.min(...vals) : null;
}

export function findHangingPieces(chess: Chess, side: 'w' | 'b'): HangingPiece[] {
  const enemy = side === 'w' ? 'b' : 'w';
  const out: HangingPiece[] = [];
  for (const row of chess.board()) {
    for (const piece of row) {
      if (!piece || piece.color !== side || piece.type === 'k' || piece.type === 'p') continue;
      const val = PIECE_VALUES[piece.type] || 0;
      const attackers = attackersOf(chess, piece.square, enemy);
      if (!attackers.length) continue;
      const defenders = attackersOf(chess, piece.square, side);
      const low = Math.min(...attackers.map((sq) => PIECE_VALUES[chess.get(sq)?.type ?? ''] ?? 99));
      if (low < val || attackers.length > defenders.length) {
        out.push({ square: piece.square, type: piece.type, value: val, attackers, defenders });
      }
    }
  }
  return out;
}

function hasMateInOne(chess: Chess, side: 'w' | 'b'): boolean {
  if (chess.turn() !== side) return false;
  return chess.moves({ verbose: true }).some((m) => String(m.san || '').includes('#'));
}

/** Может ли соперник реально забрать висящую фигуру без немедленного наказания. */
export function hangingCaptureViable(fen: string, hanging: HangingPiece, side: 'w' | 'b'): boolean {
  const candidates = hanging.attackers
    .map((from) => ({ from, value: PIECE_VALUES[new Chess(fen).get(from)?.type ?? ''] ?? 99 }))
    .sort((a, b) => a.value - b.value);
  for (const cand of candidates) {
    try {
      const c = new Chess(fen);
      const beforeType = c.get(cand.from)?.type;
      const made = c.move({ from: cand.from, to: hanging.square, promotion: 'q' });
      if (!made) continue;
      const recapture = lowestAttackerValue(c, hanging.square, side);
      if (recapture != null && recapture < cand.value) continue;
      if (hanging.value < 5 && hasMateInOne(c, side)) continue;
      if (beforeType) return true;
    } catch {
      /* try next attacker */
    }
  }
  return false;
}
